import { useCampaignMembers } from "@/hooks";
import type { CampaignSummary } from "@/types/campaign";
import { CAMPAIGN_STATUS_LABELS, MEMBER_ROLE_LABELS } from "@/types/campaign";
import { NoRealData } from "@/components/shared";

interface Props {
  campanhas: CampaignSummary[];
}

/** Equipe do projeto = membros reais das campanhas de campo vinculadas a ele. */
export default function ProjectTeamCards({ campanhas }: Props) {
  if (campanhas.length === 0) {
    return (
      <NoRealData
        icon="ri-team-line"
        title="Nenhuma campanha vinculada"
        description="A equipe do projeto é formada pelos membros das campanhas de campo. Este projeto ainda não tem campanha."
      />
    );
  }
  return (
    <div className="space-y-5">
      {campanhas.map((c) => (
        <CampaignTeam key={c.id} campanha={c} />
      ))}
    </div>
  );
}

function CampaignTeam({ campanha }: { campanha: CampaignSummary }) {
  const { members, loading, error } = useCampaignMembers(campanha.id);

  return (
    <div>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <h4 className="text-sm font-semibold text-foreground-900 font-heading">{campanha.name}</h4>
        <span className="text-xs font-mono text-foreground-500">{campanha.code}</span>
        <span className="badge bg-background-100 text-foreground-600">
          {CAMPAIGN_STATUS_LABELS[campanha.status] ?? campanha.status}
        </span>
      </div>
      {loading ? (
        <p className="text-xs text-foreground-500">Carregando membros...</p>
      ) : error ? (
        <p className="text-xs text-danger-600">Não foi possível carregar os membros: {error}</p>
      ) : members.length === 0 ? (
        <p className="text-xs text-foreground-500">Nenhum membro cadastrado nesta campanha.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {members.map((m) => {
            const nome = m.memberName ?? "Membro sem nome";
            const initials = nome.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();
            return (
              <div
                key={m.id}
                className={`bg-white rounded-card border border-background-200 p-4 ${m.isActive ? "" : "opacity-60"}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 bg-background-100">
                    <span className="text-sm font-bold text-foreground-600">{initials}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-foreground-900">{nome}</p>
                      {!m.isActive && <span className="badge bg-background-100 text-foreground-500 text-[10px]">Inativo</span>}
                    </div>
                    <p className="text-xs text-foreground-500 mt-0.5">{MEMBER_ROLE_LABELS[m.memberRole] ?? m.memberRole}</p>
                    {m.professionalCouncil && m.professionalRegistration && (
                      <p className="text-xs text-foreground-500 mt-0.5">
                        {m.professionalCouncil} {m.professionalRegistration}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
